import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { clearSession, getToken, TOKEN_KEY, USER_KEY } from 'utils/axios';
import { PartnerSession, PartnerUser } from 'types/partner';

type AuthContextType = {
  isLoggedIn: boolean;
  user: PartnerUser | null;
  /** Stores a session returned by sign-in verify or invitation accept. */
  startSession: (session: PartnerSession) => void;
  logout: () => void;
};

/** True when the JWT is malformed or its `exp` has passed. */
export const isTokenExpired = (token: string | null): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' && payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
};

const readUser = (): PartnerUser | null => {
  if (isTokenExpired(getToken())) {
    clearSession();
    return null;
  }
  try {
    return JSON.parse(sessionStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => !isTokenExpired(getToken()));
  const [user, setUser] = useState<PartnerUser | null>(readUser);

  const startSession = useCallback((session: PartnerSession) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, session.token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(session.user));
    } catch {
      // storage blocked: the session still works until the tab reloads
    }
    setUser(session.user);
    setIsLoggedIn(true);
  }, []);

  // Cached answers belong to the partner that fetched them; never show them to the next login.
  const logout = useCallback(() => {
    clearSession();
    queryClient.clear();
    setUser(null);
    setIsLoggedIn(false);
  }, [queryClient]);

  const value = useMemo(() => ({ isLoggedIn, user, startSession, logout }), [isLoggedIn, user, startSession, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
